/**
 * Component Visibility Hook
 * Handles conditional visibility logic for checklist components
 */

import { useCallback, useMemo } from "react";
import type { ChecklistComponent, ChecklistScalarValue } from "@/types/quote.types";

export const useComponentVisibility = (resolvedResponses: Record<string, ChecklistScalarValue>) => {
  const parseVisibilityCondition = (condition?: string | null) => {
    if (!condition) return undefined;
    try {
      return typeof condition === "string" ? JSON.parse(condition) : condition;
    } catch (error) {
      console.warn("Invalid visibility condition", condition, error);
      return undefined;
    }
  };

  const evaluateCondition = useCallback(
    (condition: any): boolean => {
      if (!condition || typeof condition !== "object") {
        return true;
      }

      const { field, operator, value, logic, conditions } = condition;

      if (field && operator) {
        const fieldValue = resolvedResponses[field];

        switch (operator) {
          case "equals":
            return fieldValue === value;
          case "not_equals":
            return fieldValue !== value;
          case "contains":
            if (Array.isArray(fieldValue)) {
              return fieldValue.includes(value);
            }
            return typeof fieldValue === "string" && fieldValue.includes(value);
          case "greater_than":
            return Number(fieldValue) > Number(value);
          case "less_than":
            return Number(fieldValue) < Number(value);
          case "is_empty":
            if (Array.isArray(fieldValue)) {
              return fieldValue.length === 0;
            }
            return fieldValue === undefined || fieldValue === null || fieldValue === "";
          case "is_not_empty":
            if (Array.isArray(fieldValue)) {
              return fieldValue.length > 0;
            }
            return fieldValue !== undefined && fieldValue !== null && fieldValue !== "";
          default:
            return true;
        }
      }

      if (conditions && Array.isArray(conditions)) {
        const evaluated = conditions.map((nested: any) => evaluateCondition(nested));
        switch ((logic || "and").toLowerCase()) {
          case "and":
            return evaluated.every(Boolean);
          case "or":
            return evaluated.some(Boolean);
          case "not":
            return !evaluated[0];
          default:
            return true;
        }
      }

      return true;
    },
    [resolvedResponses]
  );

  const isComponentVisible = useCallback(
    (component: ChecklistComponent): boolean => {
      if (!component.visibility_condition) {
        return true;
      }

      const condition = parseVisibilityCondition(component.visibility_condition);
      return evaluateCondition(condition);
    },
    [evaluateCondition]
  );

  return { isComponentVisible, evaluateCondition };
};
